import styled from '@emotion/styled';
import {
  Button,
  Checkbox,
  colors,
  SearchBar,
  SelectInput,
  Text
} from '@innovationdepartment/proxima-ui';
import type { Table as TableInstance } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import type { FetchAllBrandTag } from 'types/brandTag';
import type { FilterConfig } from './BrandTagList.types';
import { ALL_OPTIONS, EMPTY_OPTION } from './BrandTags.constants';
import {
  PageWrapper,
  SortIndicator,
  Table,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow
} from './BrandTags.styles';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchSection = styled.div`
  width: 100%;
`;

const FiltersSection = styled.div`
  border: 1px solid ${colors.neutral20};
  border-radius: 8px;
  padding: 16px;
  background-color: ${colors.neutral5};
`;

const FiltersTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const FiltersTitle = styled(Text)`
  text-transform: uppercase;
  font-weight: 600;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 8px;
  align-items: center;
`;

const FilterRowRight = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  width: 200px;
`;

const FilterLabel = styled(Text)`
  font-weight: 500;
  min-width: 80px;
`;

const CheckboxLabel = styled(Text)`
  font-weight: 500;
  white-space: nowrap;
`;

const TableWrapper = styled.div<{ height?: string }>`
  flex: ${props => (props.height ? 'none' : '1')};
  height: ${props => props.height || 'auto'};
  overflow-y: auto;
  background-color: ${colors.neutral5};
`;

const CheckmarkCell = styled(TableCell)`
  text-align: center;
`;

const TagCount = styled(Text)`
  color: ${colors.neutral60};
  background-color: white;
  padding: 4px 8px;
  border-radius: 4px;
`;

export interface BrandTagListViewProps {
  table: TableInstance<FetchAllBrandTag>;
  onSearch: (value: string) => void;
  onCreateTag: () => void;
  onFilterChange: (filter: string, checked: boolean) => void;
  filters: {
    personaEligible: boolean;
    sensitive: boolean;
    hideDeprecated: boolean;
  };
  categoryOptions: string[];
  subCategoryOptions: string[];
  genderOptions: string[];
  selectedCategory: string;
  selectedSubCategory: string;
  selectedGender: string;
  onCategoryChange: (value: string) => void;
  onSubCategoryChange: (value: string) => void;
  onGenderChange: (value: string) => void;
  onRowClick: (tag: FetchAllBrandTag) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  resetKey: number;
  subCategoryKey: number;
  filterConfig: FilterConfig;
  tableHeight?: string;
  showHeader: boolean;
}

const BrandTagListView = ({
  table,
  onSearch,
  onCreateTag,
  onFilterChange,
  filters,
  categoryOptions,
  subCategoryOptions,
  genderOptions,
  selectedCategory,
  selectedSubCategory,
  selectedGender,
  onCategoryChange,
  onSubCategoryChange,
  onGenderChange,
  onRowClick,
  onClearFilters,
  hasActiveFilters,
  resetKey,
  subCategoryKey,
  filterConfig,
  tableHeight,
  showHeader
}: BrandTagListViewProps) => (
  <PageWrapper>
    {!!showHeader && (
      <Header>
        <Title>
          <Text variant="h5">TAG MANAGER</Text>
        </Title>
        <Button label="Create New Tag" size="small" color="success" onClick={onCreateTag} />
      </Header>
    )}

    {!!filterConfig.search && (
      <SearchSection>
        <SearchBar
          key={`search-${resetKey}`}
          placeholder="Search tags in table..."
          onChange={onSearch}
          data-testid="search-input"
        />
      </SearchSection>
    )}

    {!!(
      filterConfig.category ||
      filterConfig.subCategory ||
      filterConfig.gender ||
      filterConfig.personaEligible ||
      filterConfig.sensitive ||
      filterConfig.hideDeprecated
    ) && (
      <FiltersSection data-testid="filters-section">
        <FiltersTitleRow>
          <FiltersTitle variant="body2">FILTERS</FiltersTitle>
        </FiltersTitleRow>
        <FilterRow>
          {!!filterConfig.category && (
            <FilterGroup>
              <FilterLabel variant="body2">Category:</FilterLabel>
              <SelectInput
                key={`category-${resetKey}`}
                options={[
                  { label: 'All', value: ALL_OPTIONS },
                  ...categoryOptions.map(cat => ({ label: cat, value: cat }))
                ]}
                value={selectedCategory}
                onChange={value => onCategoryChange(value as string)}
              />
            </FilterGroup>
          )}
          {!!filterConfig.subCategory && (
            <FilterGroup>
              <FilterLabel variant="body2">Sub-category:</FilterLabel>
              <SelectInput
                key={`subcategory-${subCategoryKey}-${resetKey}`}
                options={[
                  { label: 'All', value: ALL_OPTIONS },
                  ...subCategoryOptions.map(sub =>
                    sub === ''
                      ? { label: '<None>', value: EMPTY_OPTION }
                      : { label: sub, value: sub }
                  )
                ]}
                value={selectedSubCategory}
                onChange={value =>
                  onSubCategoryChange(value === EMPTY_OPTION ? '' : (value as string))
                }
              />
            </FilterGroup>
          )}
          {!!filterConfig.gender && (
            <FilterGroup>
              <FilterLabel variant="body2">Gender:</FilterLabel>
              <SelectInput
                key={`gender-${resetKey}`}
                options={[
                  { label: 'All', value: ALL_OPTIONS },
                  ...genderOptions.map(gender => ({ label: gender, value: gender }))
                ]}
                value={selectedGender}
                onChange={value => onGenderChange(value as string)}
              />
            </FilterGroup>
          )}
        </FilterRow>
        <FilterRow>
          {!!filterConfig.personaEligible && (
            <FilterGroup>
              <CheckboxLabel variant="body2Medium">Persona Eligible:</CheckboxLabel>
              <Checkbox
                key={`personaEligible-${resetKey}`}
                checked={filters.personaEligible}
                onChange={(checked: boolean) => onFilterChange('personaEligible', checked)}
                data-testid="persona-eligible-checkbox"
              />
            </FilterGroup>
          )}
          {!!filterConfig.sensitive && (
            <FilterGroup>
              <CheckboxLabel variant="body2Medium">Sensitive:</CheckboxLabel>
              <Checkbox
                key={`sensitive-${resetKey}`}
                checked={filters.sensitive}
                onChange={(checked: boolean) => onFilterChange('sensitive', checked)}
                data-testid="sensitive-checkbox"
              />
            </FilterGroup>
          )}
          {!!filterConfig.hideDeprecated && (
            <FilterGroup>
              <CheckboxLabel variant="body2Medium">Hide Deprecated:</CheckboxLabel>
              <Checkbox
                key={`hide-deprecated-${resetKey}`}
                checked={filters.hideDeprecated}
                onChange={(checked: boolean) => onFilterChange('hideDeprecated', checked)}
                data-testid="hide-deprecated-checkbox"
              />
            </FilterGroup>
          )}
          <FilterRowRight>
            <Button
              label="Clear Filters"
              onClick={onClearFilters}
              disabled={!hasActiveFilters}
              data-testid="clear-filters-button"
            />
          </FilterRowRight>
        </FilterRow>
      </FiltersSection>
    )}

    <TableWrapper height={tableHeight}>
      <Table>
        <TableHeader>
          <tr>
            <th colSpan={8}>
              <TagCount variant="body2">Showing {table.getRowModel().rows.length} tags</TagCount>
            </th>
          </tr>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableHeaderCell
                  key={header.id}
                  sortable={header.column.getCanSort()}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                  {{
                    asc: <SortIndicator>↑</SortIndicator>,
                    desc: <SortIndicator>↓</SortIndicator>
                  }[header.column.getIsSorted() as string] ?? null}
                </TableHeaderCell>
              ))}
            </tr>
          ))}
        </TableHeader>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <TableRow key={row.id} onClick={() => onRowClick(row.original)}>
              {row.getVisibleCells().map(cell => {
                const isCheckmarkColumn =
                  cell.column.id === 'personaEligible' ||
                  cell.column.id === 'sensitive' ||
                  cell.column.id === 'deprecated';
                const CellComponent = isCheckmarkColumn ? CheckmarkCell : TableCell;

                return (
                  <CellComponent key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </CellComponent>
                );
              })}
            </TableRow>
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  </PageWrapper>
);

export default BrandTagListView;
